/**
 * Resolves unit price for a quantity (mirrors backend ProductPricing).
 * Uses the tier whose inclusive [minQuantity, maxQuantity] contains the quantity
 * (missing/null maxQuantity means no upper bound).
 */
export function resolveVolumeUnitPrice(product, quantity) {
    const qty = Math.max(1, Number(quantity) || 1);
    if (!product?.hasVolumePricing || !product.volumePrices?.length) {
        return Number(product?.price || 0);
    }
    for (const tier of formatVolumePriceTiers(product.volumePrices)) {
        if (qty >= tier.minQuantity && (tier.maxQuantity == null || qty <= tier.maxQuantity)) {
            return tier.price;
        }
    }
    return Number(product.price || 0);
}

/** Lowest unit price among volume tiers (for product list cards). */
export function lowestProductPrice(product) {
    if (!product?.hasVolumePricing || !product.volumePrices?.length) {
        return Number(product?.price || 0);
    }
    const prices = formatVolumePriceTiers(product.volumePrices)
        .map((tier) => tier.price)
        .filter((price) => Number.isFinite(price));
    if (!prices.length) {
        return Number(product?.price || 0);
    }
    return Math.min(...prices);
}

export function formatVolumePriceTiers(volumePrices) {
    if (!volumePrices?.length) {
        return [];
    }
    return [...volumePrices]
        .map((tier) => ({
            minQuantity: Number(tier.minQuantity),
            maxQuantity: tier.maxQuantity == null || tier.maxQuantity === ""
                ? null
                : Number(tier.maxQuantity),
            price: Number(tier.price)
        }))
        .sort((a, b) => {
            if (a.minQuantity !== b.minQuantity) {
                return a.minQuantity - b.minQuantity;
            }
            const aMax = a.maxQuantity == null ? Number.MAX_SAFE_INTEGER : a.maxQuantity;
            const bMax = b.maxQuantity == null ? Number.MAX_SAFE_INTEGER : b.maxQuantity;
            return aMax - bMax;
        });
}

export function formatVolumeRangeLabel(tier) {
    if (tier.maxQuantity == null) {
        return `À partir de ${tier.minQuantity}`;
    }
    if (tier.minQuantity === tier.maxQuantity) {
        return `Quantité ${tier.minQuantity}`;
    }
    return `De ${tier.minQuantity} à ${tier.maxQuantity}`;
}

/** Returns an error message if ranges overlap or are invalid; otherwise "". */
export function validateVolumePriceRanges(volumePrices) {
    const tiers = formatVolumePriceTiers(volumePrices);
    if (!tiers.length) {
        return "Au moins un palier de prix est obligatoire";
    }
    for (const tier of tiers) {
        if (!Number.isFinite(tier.minQuantity) || tier.minQuantity < 1) {
            return "La quantité minimale d'un palier doit être au moins 1";
        }
        if (tier.maxQuantity != null && tier.maxQuantity < tier.minQuantity) {
            return "La quantité maximale doit être supérieure ou égale à la quantité minimale";
        }
        if (!Number.isFinite(tier.price) || tier.price < 0) {
            return "Le prix d'un palier est obligatoire";
        }
    }
    const openEnded = tiers.filter((tier) => tier.maxQuantity == null);
    if (openEnded.length > 1) {
        return "Un seul palier peut être sans quantité maximale (illimité)";
    }
    if (openEnded.length === 1 && tiers[tiers.length - 1].maxQuantity != null) {
        return "Le palier sans quantité maximale doit être le dernier";
    }
    if (!tiers.some((tier) => tier.minQuantity === 1)) {
        return "Le tarif dégressif doit inclure un palier pour la quantité 1";
    }
    for (let i = 0; i < tiers.length; i++) {
        const left = tiers[i];
        const leftMax = left.maxQuantity == null ? Number.MAX_SAFE_INTEGER : left.maxQuantity;
        for (let j = i + 1; j < tiers.length; j++) {
            const right = tiers[j];
            const rightMax = right.maxQuantity == null ? Number.MAX_SAFE_INTEGER : right.maxQuantity;
            if (left.minQuantity <= rightMax && right.minQuantity <= leftMax) {
                return `Les plages de quantité se chevauchent (${formatVolumeRangeLabel(left)} et ${formatVolumeRangeLabel(right)})`;
            }
        }
    }
    return "";
}
